import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';

import StarsButton from '@swf/components/StarsButton/StarsButton';

import ArrowLeft from '../../assets/icons/chevron-left.svg';
import ArrowRight from '../../assets/icons/chevron-right.svg';
import { PASSES_TITLE_TRANSLATIONS } from '../../constants';
import useCarousel from '../../hooks/useCarousel';
import type { PassTypeKey } from '../../types';

import styles from './Row.scss';

interface OwnProps {
  action?: React.ReactNode;
  className?: string;
  passType: PassTypeKey;
  dataTestId?: string;
}

type Props = React.PropsWithChildren<OwnProps>;

const Row = ({ action, children, className, dataTestId, passType }: Props) => {
  const { scrollLeft, scrollRight, carouselRef, containerRef, isLeftEnd, isRightEnd, offset } =
    useCarousel({ children, gap: 10 });

  const items = React.useMemo(
    () =>
      React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return null;
        }

        return (
          <div className={classNames(styles.child, styles.aspectRatio)} data-testid="card">
            {child}
          </div>
        );
      }),
    [children]
  );

  return (
    <div className={classNames(styles.container, className)} data-testid={dataTestId}>
      <div className={styles.root}>
        <h2 className={classNames(styles.title, action && styles.titleAction)}>
          <Translation tagName="span" {...PASSES_TITLE_TRANSLATIONS[passType]} />
        </h2>
        <div className={styles.arrowButtons}>
          <StarsButton
            data-testid="nav-left"
            className={styles.arrowButton}
            onClick={scrollLeft}
            disabled={isLeftEnd}
            variant="text"
            icon={<ArrowLeft />}
          />
          <StarsButton
            data-testid="nav-right"
            className={styles.arrowButton}
            onClick={scrollRight}
            disabled={isRightEnd}
            variant="text"
            icon={<ArrowRight />}
          />
        </div>
      </div>
      <div className={styles.carouselContainer} ref={containerRef}>
        <div
          className={styles.carousel}
          ref={carouselRef}
          style={{ transform: `translate(-${offset}px, 0px)` }}
        >
          {items}
        </div>
      </div>
    </div>
  );
};

export default Row;
